import React, { useEffect, useState } from "react";
import { db } from "../../firebase";
import { collection, query, where, getDocs } from "firebase/firestore";
import { fetchUsersByUserIds, tolerantId } from "../../utils/firestoreHelpers";
import { useBuilding } from "../../context/BuildingContext";
import Loader from "../../components/Loader";
import { FaBuilding, FaDoorOpen, FaUsers } from "react-icons/fa";
import ScreenHeader from "../../components/ScreenHeader";
import StatusBadge from "../../components/StatusBadge";
import { colors, shadows, radius, spacing, typography, transitions, cardBase, pageContainer } from "../../utils/designTokens";
import {
  computeAccountState,
  ACCOUNT_STATE_BADGE_KEYS,
} from "../../utils/accountState";

const Residentes = ({ setSelected, setUnidadSeleccionada, setUnidadBackTo }) => {
  const { buildings, selectedBuilding, setSelectedBuilding } = useBuilding();
  const [apartments, setApartments] = useState([]);
  const [residents, setResidents] = useState([]);
  const [loading, setLoading] = useState(true);

  // Usar el filtro global del contexto
  const buildingsToProcess = selectedBuilding 
    ? buildings.filter(b => b.buildingid === selectedBuilding.buildingid)
    : buildings;

  useEffect(() => {
    if (!buildings || buildings.length === 0) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        const fetchForBuilding = async (bId) => {
          const q = query(
            collection(db, "apartments"),
            where("building", "in", tolerantId(bId))
          );
          const snapshot = await getDocs(q);
          return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        };

        let gathered = [];
        let residentsList = [];
        for (const b of buildingsToProcess) {
          const data = await fetchForBuilding(b.buildingid);
          gathered = gathered.concat(data);

          // SEC-06: solo residentes que aparecen como owner/tenant en las unidades
          // visibles (no todos los typeuser 0). C10 (reglas 0D): las fichas
          // ajenas se leen por edificio, con su accessBids (fetchUsersByUserIds
          // con el edificio). Si un edificio falla, las unidades salen igual.
          const userIds = new Set();
          data.forEach((apt) => {
            if (apt.owner != null && apt.owner !== "") userIds.add(String(apt.owner));
            if (apt.tenant != null && apt.tenant !== "") userIds.add(String(apt.tenant));
          });
          if (userIds.size === 0) continue;
          try {
            residentsList = residentsList.concat(await fetchUsersByUserIds([...userIds], b.buildingid));
          } catch (err) {
            console.warn(`[Residentes] Sin las fichas del edificio ${b.buildingid}:`, err?.code || err?.message);
          }
        }
        gathered.sort((a, b) => a.floor - b.floor || String(a.number).localeCompare(String(b.number)));
        setApartments(gathered);
        setResidents(residentsList);
      } catch (err) {
        console.error("Error al cargar apartamentos:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [buildings, selectedBuilding]);

  const getResidentLabel = (userid) => {
    if (!userid) return "-";
    const r = residents.find((res) => String(res.userid) === String(userid));
    return r ? `${r.name} (${r.email})` : `ID: ${userid}`;
  };

  // Bloque 1 + 5 — render del residente con badge del estado derivado.
  // Si no encontramos el doc, no muestra badge (caso ID huérfano).
  const renderResident = (userid, fallbackLabel) => {
    if (!userid) return <span>{fallbackLabel || "-"}</span>;
    const r = residents.find((res) => String(res.userid) === String(userid));
    if (!r) return <span>ID: {userid}</span>;
    const state = computeAccountState(r);
    const badgeKey = ACCOUNT_STATE_BADGE_KEYS[state];
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span>{r.name} ({r.email})</span>
        {badgeKey && <StatusBadge status={badgeKey} />}
      </span>
    );
  };

  if (loading) return <Loader />;

  return (
    <div style={styles.page}>
      {/* Header con título y acciones */}
      <ScreenHeader icon={FaUsers} title="Unidades" subtitle="Gestión de unidades del edificio" />

      {apartments.length === 0 ? (
        <div style={styles.emptyState}>
          <FaDoorOpen size={40} color={colors.gray300} />
          <p style={styles.emptyText}>No hay unidades para este edificio.</p>
        </div>
      ) : !selectedBuilding ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
          {buildingsToProcess.map((b) => {
            const group = apartments.filter((a) => String(a.building) === String(b.buildingid));
            if (group.length === 0) return null;
            return (
              <div key={b.buildingid}>
                <div style={styles.groupHeader}>
                  <FaBuilding size={16} color={colors.primary} />
                  <h2 style={styles.groupTitle}>{b.name} - {b.city}</h2>
                  <span style={styles.groupCount}>{group.length} unidades</span>
                </div>
                <div style={styles.grid} data-tutorial="unidades-grilla">
                  {group.map((apt) => (
                    <button
                      type="button"
                      key={apt.id}
                      style={styles.cardButton}
                      onClick={() => {
                        if (setUnidadBackTo) setUnidadBackTo("residentes");
                        setUnidadSeleccionada(apt.id);
                        setSelected("unidad");
                      }}
                      aria-label={`Ver detalle de Unidad ${apt.number}, Piso ${apt.floor}`}
                    >
                      <div style={styles.cardHeader}>
                        <h3 style={styles.cardTitle}>Unidad {apt.number}</h3>
                        <span style={styles.cardBadge}>Piso {apt.floor}</span>
                      </div>
                      <div style={styles.cardInfo}>
                        <p>Dormitorios: {apt.bedroom || "-"}</p>
                        <p>Baños: {apt.bathroom || "-"}</p>
                        <p>M²: {apt.m2 || "-"}</p>
                        {apt.owner ? (
                          <p>Propietario: {renderResident(apt.owner)}</p>
                        ) : (
                          <p style={styles.warningBadge}>Sin propietario asignado</p>
                        )}
                        {apt.tenant ? (
                          <p>Inquilino: {renderResident(apt.tenant)}</p>
                        ) : (
                          <p>Inquilino: —</p>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div style={styles.grid} data-tutorial="unidades-grilla">
          {apartments.map((apt) => (
            <button
              type="button"
              key={apt.id}
              style={styles.cardButton}
              onClick={() => {
                setUnidadSeleccionada(apt.id);
                setSelected("unidad");
              }}
              aria-label={`Ver detalle de Unidad ${apt.number}, Piso ${apt.floor}`}
            >
              <div style={styles.cardHeader}>
                <h3 style={styles.cardTitle}>Unidad {apt.number}</h3>
                <span style={styles.cardBadge}>Piso {apt.floor}</span>
              </div>
              <div style={styles.cardInfo}>
                <p>Dormitorios: {apt.bedroom || "-"}</p>
                <p>Baños: {apt.bathroom || "-"}</p>
                <p>M²: {apt.m2 || "-"}</p>
                {apt.owner ? (
                  <p>Propietario: {renderResident(apt.owner)}</p>
                ) : (
                  <p style={styles.warningBadge}>Sin propietario asignado</p>
                )}
                {apt.tenant ? (
                  <p>Inquilino: {renderResident(apt.tenant)}</p>
                ) : (
                  <p>Inquilino: —</p>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const styles = {
  // Contenedor principal
  page: {
    ...pageContainer,
  },
  pageHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: spacing[3],
  },
  headerTitleSection: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  pageTitle: {
    fontFamily: typography.fontFamily,
    fontSize: typography.sizes.xl,
    fontWeight: 400,
    color: colors.primary,
    margin: 0,
    letterSpacing: "-0.01em",
  },
  pageSubtitle: {
    fontSize: typography.sizes.base,
    color: colors.gray600,
    margin: 0,
  },
  // Barra de filtro por edificio
  filterBar: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    background: colors.white,
    border: `1px solid ${colors.gray200}`,
    borderRadius: radius.sm,
    padding: "8px 12px",
  },
  filterSelect: {
    border: "none",
    outline: "none",
    background: "transparent",
    fontSize: typography.sizes.md,
    fontWeight: 400,
    color: colors.gray900,
    cursor: "pointer",
  },
  // Estado vacío sin unidades
  emptyState: {
    ...cardBase,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: 60,
    gap: spacing[3],
  },
  emptyText: {
    color: colors.gray600,
    fontSize: typography.sizes.md,
    margin: 0,
  },
  // Cabecera de grupo de edificio
  groupHeader: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: spacing[3],
  },
  groupTitle: {
    margin: 0,
    fontSize: typography.sizes.lg,
    fontWeight: 400,
    color: colors.black,
  },
  groupCount: {
    fontSize: typography.sizes.sm,
    color: colors.gray600,
    backgroundColor: colors.gray100,
    padding: "4px 10px",
    borderRadius: radius.md,
  },
  // Grilla de tarjetas de unidades
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
    gap: spacing[4],
  },
  // Tarjeta de unidad
  card: {
    ...cardBase,
    padding: 18,
    cursor: "pointer",
  },
  // Tarjeta de unidad como botón accesible
  cardButton: {
    ...cardBase,
    padding: 18,
    cursor: "pointer",
    border: "none",
    textAlign: "left",
    width: "100%",
    display: "block",
    fontFamily: "inherit",
    fontSize: "inherit",
    color: "inherit",
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: spacing[3],
  },
  cardTitle: {
    margin: 0,
    fontSize: typography.sizes.lg,
    fontWeight: 400,
    color: colors.black,
  },
  // Badge de piso
  cardBadge: {
    backgroundColor: colors.primarySubtle,
    color: colors.primary,
    padding: "4px 10px",
    borderRadius: 6,
    fontSize: typography.sizes.sm,
    fontWeight: 400,
  },
  cardInfo: {
    fontSize: typography.sizes.base,
    color: colors.gray600,
    lineHeight: 1.6,
  },
  // Badge de advertencia (sin propietario)
  warningBadge: {
    color: colors.error,
    fontWeight: 400,
    fontSize: typography.sizes.sm,
    background: colors.warningLight,
    padding: "4px 8px",
    borderRadius: 6,
    display: "inline-block",
    marginTop: 4,
  },
};

export default Residentes;
